/**
 * 위험요인 해소 표시 섹션
 * 해소된 발견사항의 증빙 사진 + 조치사항을 표시
 * 점검 상세, 보고서(화면/PDF) 3곳에서 공통 사용
 */
import { useState } from 'react';
import { PhotoViewer } from './PhotoViewer';
import { normalizeFileUrl } from '../../../lib/api';
import { renderNotes, renderNotesPrint } from './ActionItemsInput';

interface ResolutionPhoto {
  url: string;
  thumbnailUrl?: string;
  fileId?: string;
}

interface HazardResolutionSectionProps {
  /** 다중 해소 증빙 사진 (우선) */
  resolutionPhotos?: ResolutionPhoto[];
  /** 레거시 단일 해소 사진 URL (fallback) */
  resolutionPhotoUrl?: string;
  /** 조치사항 메모 */
  notes?: string;
  /** 처리자 이름 */
  resolvedByName?: string;
  /** 해소 일시 (ISO 문자열) */
  resolvedAt?: string;
  /** 수정 가능 여부 */
  editable?: boolean;
  /** 수정 버튼 클릭 핸들러 */
  onEdit?: () => void;
  /** 렌더링 모드: 화면(Tailwind) 또는 PDF(시맨틱 CSS) */
  mode?: 'screen' | 'print';
  /** 미저장 상태 표시 (로컬 버퍼링 중) */
  unsaved?: boolean;
}

/**
 * 표시할 사진 목록을 결정 (resolutionPhotos 우선, 없으면 resolutionPhotoUrl fallback)
 */
function getPhotos(
  resolutionPhotos?: ResolutionPhoto[],
  resolutionPhotoUrl?: string,
): ResolutionPhoto[] {
  if (resolutionPhotos && resolutionPhotos.length > 0) {
    return resolutionPhotos;
  }
  if (resolutionPhotoUrl) {
    return [{ url: resolutionPhotoUrl }];
  }
  return [];
}

export function HazardResolutionSection({
  resolutionPhotos,
  resolutionPhotoUrl,
  notes,
  resolvedByName,
  resolvedAt,
  editable = false,
  onEdit,
  mode = 'screen',
  unsaved = false,
}: HazardResolutionSectionProps) {
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const photos = getPhotos(resolutionPhotos, resolutionPhotoUrl);
  const hasPhotos = photos.length > 0;

  // 표시할 내용이 없으면 렌더링하지 않음
  if (!hasPhotos && !notes) return null;

  const formattedDate = resolvedAt
    ? new Date(resolvedAt).toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : undefined;

  // Print 모드 (시맨틱 CSS)
  if (mode === 'print') {
    return (
      <div className="finding-resolution-section">
        {hasPhotos && (
          <div className="resolution-photos-vertical">
            <img
              src={normalizeFileUrl(photos[0].url)}
              alt="해소 사진"
              className="resolution-photo-large"
            />
          </div>
        )}
        <div className="resolution-details">
          {notes && (
            <div className="resolution-notes">
              <span className="resolution-notes-label">조치사항:</span>
              {renderNotesPrint(notes)}
            </div>
          )}
          <div className="resolution-meta">
            {resolvedByName && (
              <span>처리자: {resolvedByName}</span>
            )}
            {resolvedAt && (
              <span style={{ marginLeft: resolvedByName ? '8px' : '0' }}>
                {formattedDate}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Screen 모드 (Tailwind)
  return (
    <>
      <div className="mt-3 bg-green-50 border border-green-200 rounded-card p-3">
        {/* 수정 버튼 (편집 모드에서만) */}
        {(editable && onEdit || unsaved) && (
          <div className="flex items-center justify-between mb-2">
            {unsaved && <span className="text-xs font-medium text-orange-500">(미저장)</span>}
            {editable && onEdit && (
              <button
                type="button"
                data-action-button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="text-xs text-brand-600 hover:text-brand-800 font-medium ml-auto"
              >
                수정
              </button>
            )}
          </div>
        )}

        {/* 사진: 대표 1장 + 나머지 개수 표시 */}
        {hasPhotos && (
          <div className="mb-2">
            <button
              type="button"
              onClick={() => setViewerIndex(0)}
              className="w-full rounded-card-sm overflow-hidden border border-green-200 hover:border-green-400 transition-colors cursor-pointer relative"
            >
              <img
                src={normalizeFileUrl(photos[0].thumbnailUrl || photos[0].url)}
                alt="해소 사진"
                className="max-w-full max-h-52 object-contain"
              />
              {photos.length > 1 && (
                <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                  +{photos.length - 1}장
                </span>
              )}
            </button>
          </div>
        )}

        {/* 조치사항 + 메타정보 (마지막 사진 아래) */}
        {notes && (
          <div className="mb-1.5">
            <span className="text-xs text-green-600 font-medium">조치사항:</span>
            <div className="text-sm text-ink-700 mt-0.5">{renderNotes(notes)}</div>
          </div>
        )}
        <div className="flex items-center gap-2 text-xs text-ink-500 flex-wrap">
          {formattedDate && <span>{formattedDate}</span>}
          {resolvedByName && (
            <>
              {formattedDate && <span>·</span>}
              <span>처리자: {resolvedByName}</span>
            </>
          )}
        </div>
      </div>

      {/* 사진 확대 뷰어 (썸네일 포함) */}
      {viewerIndex !== null && (
        <PhotoViewer
          photos={photos.map((p, i) => ({
            id: p.fileId || `res-${i}`,
            url: normalizeFileUrl(p.url),
            thumbnailUrl: p.thumbnailUrl ? normalizeFileUrl(p.thumbnailUrl) : undefined,
          }))}
          currentIndex={viewerIndex}
          isOpen={true}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </>
  );
}

export default HazardResolutionSection;
